import { memo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import { popupGetMessageListRequest } from "providers/ChatPopupProvider/slice";
import MessageCell from "containers/Dashboard/Fonehouse/Messenger/MessageList/MessageCell";

const ChatPopupMessageList = ({ conversationId }) => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const prevIdsLengthRef = useRef(0);
  const windowMessages = useSelector(
    (state) => state.chatPopup.windowMessages[conversationId]
  );

  const ids = windowMessages?.ids || [];
  const entities = windowMessages?.entities || {};
  const hasMore = windowMessages?.hasMore || false;
  const isLoading = windowMessages?.isLoading || false;
  const lastMessageTime = windowMessages?.lastMessageTime || null;

  // Load initial messages
  useEffect(() => {
    if (conversationId) {
      dispatch(
        popupGetMessageListRequest({
          limit: 15,
          conversationId,
        })
      );
    }
  }, [conversationId, dispatch]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (ids.length > prevIdsLengthRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevIdsLengthRef.current = ids.length;
  }, [ids.length]);

  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      dispatch(
        popupGetMessageListRequest({
          limit: 15,
          time: lastMessageTime,
          conversationId,
        })
      );
    }
  }, [dispatch, isLoading, hasMore, lastMessageTime, conversationId]);

  // Sort messages newest first (for reverse display)
  const sortedIds = [...ids].sort((a, b) => {
    const dateA = entities[a]?.createdAt || "";
    const dateB = entities[b]?.createdAt || "";
    return dateB.localeCompare(dateA);
  });

  // Group messages by date
  let lastDate = null;

  return (
    <div
      className="chat-popup-message-list"
      id={`chat-popup-messages-${conversationId}`}
      ref={scrollRef}
    >
      <InfiniteScroll
        dataLength={sortedIds.length}
        next={handleLoadMore}
        hasMore={hasMore}
        loader={
          <div style={{ textAlign: "center", padding: 4 }}>
            <Spin size="small" />
          </div>
        }
        scrollableTarget={`chat-popup-messages-${conversationId}`}
        inverse={true}
        style={{ display: "flex", flexDirection: "column-reverse" }}
        scrollThreshold="50px"
      >
        {sortedIds.map((id, index) => {
          const message = entities[id];
          if (!message) return null;
          const isOwnCell = message.isAdmin === 1;
          const messageDate = moment(message.createdAt).format("DD/MM/YYYY");
          const showDateHeader = messageDate !== lastDate;
          lastDate = messageDate;

          // Check if should show avatar (different sender or >5min gap)
          const nextMsg = sortedIds[index + 1]
            ? entities[sortedIds[index + 1]]
            : null;
          const showAvatar =
            !nextMsg ||
            nextMsg.isAdmin !== message.isAdmin ||
            moment(message.createdAt).diff(
              moment(nextMsg?.createdAt),
              "minutes"
            ) > 5;

          return (
            <div key={id}>
              {showDateHeader && (
                <div className="chat-popup-date-header">
                  {messageDate}
                </div>
              )}
              <MessageCell
                data={message}
                isOwnCell={isOwnCell}
                showAvatar={showAvatar}
              />
            </div>
          );
        })}
      </InfiniteScroll>
      {isLoading && sortedIds.length === 0 && (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin size="small" />
        </div>
      )}
    </div>
  );
};

export default memo(ChatPopupMessageList);
